import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Save } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { tipoff } from "@/lib/format";
import { Panel } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TeamTable } from "@/components/upload/TeamTable";

export const NUM_FIELDS = ["pts", "fgm", "fga", "fg3m", "fg3a", "ftm", "fta", "reb", "ast", "tov", "stl", "blk", "plus_minus"];

export function rowErrors(p) {
  const missing = NUM_FIELDS.filter((f) => f !== "fg3a" && (p[f] === null || p[f] === "" || p[f] === undefined || Number.isNaN(Number(p[f]))));
  const formula = missing.length === 0 && Number(p.pts) !== 2 * Number(p.fgm) + Number(p.fg3m) + Number(p.ftm);
  return { missing, formula, unmatched: !p.player_id && !p.create };
}

function initTeams(result) {
  return result.teams.map((t) => ({
    ...t,
    players: t.players.map((p) => ({ ...p, player_id: p.match?.id || null, player_name: p.match?.name || null, create: false })),
  }));
}

export function ReviewPanel({ result, onSaved }) {
  const [teams, setTeams] = useState(() => initTeams(result));
  const [gameId, setGameId] = useState(result.suggested_game_id || "");
  const [saving, setSaving] = useState(false);
  const game = result.games.find((g) => g.id === gameId);

  const issues = useMemo(() => {
    let rows = 0;
    teams.forEach((t) => t.players.forEach((p) => {
      const e = rowErrors(p);
      if (e.missing.length || e.formula || e.unmatched) rows += 1;
    }));
    const teamIssue = teams.some((t) => !t.team_id || (game && ![game.home_team_id, game.away_team_id].includes(t.team_id)));
    return { rows, teamIssue, hasApi: game?.box_status === "complete" };
  }, [teams, game]);

  const updateTeam = (i, patch) => setTeams((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const save = async () => {
    const rows = teams.flatMap((t) => t.players.map((p) => ({
      team_id: t.team_id, name: p.player_name && !p.create ? p.player_name : p.name, player_id: p.create ? null : p.player_id,
      create: p.create, min: p.min, ...Object.fromEntries(NUM_FIELDS.map((f) => [f, f === "fg3a" && (p[f] === null || p[f] === "") ? null : Number(p[f])])),
    })));
    const totalFor = (teamId) => { const t = teams.find((x) => x.team_id === teamId); return t?.totals?.pts ?? null; };
    setSaving(true);
    try {
      const { data } = await api.post("/screenshots/confirm", {
        game_id: gameId, rows, home_pts: totalFor(game.home_team_id), away_pts: totalFor(game.away_team_id),
      });
      toast.success(`Saved ${data.saved} player rows · ${data.projections_saved} projections refreshed`);
      onSaved();
    } catch (e) { toast.error(errorText(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="review-panel">
      <Panel className="p-5 flex flex-wrap items-center gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Game</div>
          <Select value={gameId} onValueChange={setGameId}>
            <SelectTrigger data-testid="review-game-select" className="w-72 mt-1 bg-slate-900/70"><SelectValue placeholder="Pick the game" /></SelectTrigger>
            <SelectContent>
              {result.games.map((g) => <SelectItem key={g.id} value={g.id}>{g.away_code} @ {g.home_code} · {tipoff(g.date_utc)}{g.box_status === "complete" ? " · has ESPN box" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {result.warnings?.length > 0 && (
          <div data-testid="review-warnings" className="text-xs text-amber-300 flex gap-2 max-w-xl"><AlertTriangle className="h-4 w-4 shrink-0" />{result.warnings.join(" · ")}</div>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span data-testid="review-issue-count" className={`text-sm font-mono-num ${issues.rows || issues.teamIssue || issues.hasApi ? "text-rose-400" : "text-emerald-400"}`}>
            {issues.hasApi ? "This game already has the ESPN box score — API data wins" : issues.rows ? `${issues.rows} row(s) need attention` : issues.teamIssue ? "Match both teams to the game" : "All rows valid"}
          </span>
          <Button data-testid="confirm-save-btn" onClick={save} disabled={!game || saving || issues.rows > 0 || issues.teamIssue || issues.hasApi} className="bg-emerald-600 hover:bg-emerald-500">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Confirm & save
          </Button>
        </div>
      </Panel>
      {teams.map((t, i) => <TeamTable key={i} index={i} team={t} game={game} onChange={(patch) => updateTeam(i, patch)} />)}
    </div>
  );
}
